import { useCallback, useEffect, useRef, useState } from 'react';

const PENDING_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Manages adaptive polling and pending-generation tracking for DPA uploads.
 *
 * Starts polling every 3 s (backing off to 30 s) whenever any row is in a
 * transient state (generating / deleting) or a generation was dispatched but
 * not yet reflected in the server response.  Pauses while the tab is hidden.
 *
 * @param {Array}    uploads       - Current page of upload objects from the server.
 * @param {Function} silentRefetch - Refetch that does not trigger a loading spinner.
 * @returns {{ pendingGenerationIds: number[], addPendingGeneration: Function }}
 */
function useDpaUploadPolling(uploads, silentRefetch) {
  const [pendingGenerationIds, setPendingGenerationIds] = useState([]);
  const pendingTimestampsRef = useRef({});

  const [pollTick, setPollTick] = useState(0);
  const pollDelayRef = useRef(3000);
  const lastSigRef = useRef('');

  // Evict pending IDs once the server confirms the row moved on, the row was
  // deleted, or the 5-minute worker-claim timeout has elapsed.
  useEffect(() => {
    if (pendingGenerationIds.length === 0) return;
    const now = Date.now();
    const resolved = pendingGenerationIds.filter(id => {
      const upload = uploads.find(u => u.id === id);
      const timedOut = now - (pendingTimestampsRef.current[id] || 0) > PENDING_TIMEOUT_MS;
      return !upload || upload.status !== 'uploaded' || timedOut;
    });
    if (resolved.length > 0) {
      resolved.forEach(id => delete pendingTimestampsRef.current[id]);
      setPendingGenerationIds(ids => ids.filter(id => !resolved.includes(id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads, pendingGenerationIds]);

  // Adaptive polling: 3 s initial, doubles each tick up to 30 s, resets on
  // status change, pauses when the tab is hidden.
  useEffect(() => {
    const hasTransient =
      uploads.some(u => u.status === 'generating' || u.status === 'deleting') ||
      pendingGenerationIds.length > 0;

    if (!hasTransient) {
      pollDelayRef.current = 3000;
      return;
    }

    const sig = uploads.map(u => `${u.id}:${u.status}`).join(',');
    if (lastSigRef.current !== sig) {
      pollDelayRef.current = 3000;
      lastSigRef.current = sig;
    }

    if (document.visibilityState === 'hidden') {
      const onVisible = () => setPollTick(t => t + 1);
      document.addEventListener('visibilitychange', onVisible, { once: true });
      return () => document.removeEventListener('visibilitychange', onVisible);
    }

    const delay = pollDelayRef.current;
    const timer = setTimeout(() => {
      pollDelayRef.current = Math.min(delay * 2, 30000);
      silentRefetch();
      setPollTick(t => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [uploads, pollTick, silentRefetch, pendingGenerationIds]);

  const addPendingGeneration = useCallback(id => {
    pendingTimestampsRef.current[id] = Date.now();
    setPendingGenerationIds(ids => [...ids, id]);
    setPollTick(t => t + 1);
  }, []);

  return { pendingGenerationIds, addPendingGeneration };
}

export default useDpaUploadPolling;
